import {
  addConduit,
  addDivider,
  addJunction,
  addOrifice,
  addOutfall,
  addOutlet,
  addPump,
  addRaingage,
  addStorage,
  addWeir,
} from "../model/inp/editNetwork.ts";
import type { ElementRef } from "../model/inp/properties.ts";
import type { LinkKind, NodeKind, Point } from "../model/inp/types.ts";
import type { ElementDefaults } from "../model/elementDefaults.ts";

export type EditMode =
  | "select"
  | "add-junction"
  | "add-outfall"
  | "add-storage"
  | "add-divider"
  | "add-raingage"
  | "add-conduit"
  | "add-pump"
  | "add-orifice"
  | "add-weir"
  | "add-outlet"
  | "add-subcatchment"
  | "select-rect"
  | "select-lasso";

/**
 * Every "click the map to place one" tool (a node kind or a raingage) —
 * `handleBackgroundClick` looks a mode up here instead of a chain of
 * per-kind `if`s, so a new node kind only needs one new entry plus an icon.
 */
type AddPointTool = {
  fn: (
    raw: ReadonlyMap<string, string[][]>,
    point: Point,
    defaults?: ElementDefaults
  ) => { raw: Map<string, string[][]>; name: string };
} & ({ category: "node"; kind: NodeKind } | { category: "raingage"; kind: "raingage" });

/** Builds the `ElementRef` a just-placed ADD_POINT tool's result should select — a small helper so `category`/`kind` stay correlated through the `AddPointTool` union instead of widening to a mismatched pair `ElementRef`'s discriminated union rejects. */
export function addPointToolRef(tool: AddPointTool, name: string): ElementRef {
  return tool.category === "raingage"
    ? { category: "raingage", kind: "raingage", name }
    : { category: "node", kind: tool.kind, name };
}

export const ADD_POINT: Partial<Record<EditMode, AddPointTool>> = {
  "add-junction": { fn: addJunction, category: "node", kind: "junction" },
  "add-outfall": { fn: addOutfall, category: "node", kind: "outfall" },
  "add-storage": { fn: addStorage, category: "node", kind: "storage" },
  "add-divider": { fn: addDivider, category: "node", kind: "divider" },
  "add-raingage": { fn: addRaingage, category: "raingage", kind: "raingage" },
};

/** `addConduit`'s real signature takes an optional `length` before `defaults` (auto-length at draw time, see `handleElementClick`) — this adapter fixes it at `undefined` so "add-conduit" type-checks against ADD_LINK's shared 4-param `fn` shape below. Never actually called: `handleElementClick`'s own conduit branch always calls the real `addConduit` directly with a computed length instead. */
function addConduitViaTable(
  raw: ReadonlyMap<string, string[][]>,
  fromNode: string,
  toNode: string,
  defaults?: ElementDefaults
) {
  return addConduit(raw, fromNode, toNode, undefined, defaults);
}

/** Same idea as ADD_POINT above, for the "click two nodes to connect them" tools. */
export const ADD_LINK: Partial<
  Record<
    EditMode,
    {
      fn: (
        raw: ReadonlyMap<string, string[][]>,
        fromNode: string,
        toNode: string,
        defaults?: ElementDefaults
      ) => { raw: Map<string, string[][]>; name: string };
      kind: LinkKind;
    }
  >
> = {
  "add-conduit": { fn: addConduitViaTable, kind: "conduit" },
  "add-pump": { fn: addPump, kind: "pump" },
  "add-orifice": { fn: addOrifice, kind: "orifice" },
  "add-weir": { fn: addWeir, kind: "weir" },
  "add-outlet": { fn: addOutlet, kind: "outlet" },
};
